import { ApiClient, type ApiClientOptions } from '@/infra/api-client'
import type { AipCursorPaged } from '@/infra/paging'
import { toURLSearchParams } from '@/utils/url-util'
import {
  type ListProHistoricalTransactionsRequest,
  type ProHistoricalTransactionResource,
  type DownloadProHistoricalTransactionsAsCsvRequest,
} from '@/features/payouts/api/resources/pro-transactions'
import { type ProPayoutSummaryResource } from '@/features/payouts/api/resources/pro-analytics'
import {
  type PayoutMethodResource,
  type PayoutMethodStatusResource,
} from '@/features/payouts/api/resources/payout-method'

/**
 * Pro payouts endpoints of the BigMoney API.
 */
export class PayoutsApi {
  readonly payoutMethods
  readonly transactions
  readonly analytics

  constructor(options: ApiClientOptions) {
    this.payoutMethods = new PayoutMethodApi(options)
    this.transactions = new ProTransactionApi(options)
    this.analytics = new ProAnalyticsApi(options)
  }
}

/**
 * API for working with payout methods.
 */
export class PayoutMethodApi extends ApiClient {
  async getByUserPublicId(
    userPublicId: string,
    signal?: AbortSignal,
  ): Promise<PayoutMethodResource> {
    return this._get(
      `payouts/v1/users/${userPublicId}/payout-method`,
      undefined,
      {
        signal,
      },
    )
  }

  async getStatusByUserPublicId(
    userPublicId: string,
    signal?: AbortSignal,
  ): Promise<PayoutMethodStatusResource> {
    return this._get(
      `payouts/v1/users/${userPublicId}/payout-method/status`,
      undefined,
      { signal },
    )
  }

  /**
   * Connect stripe method account with a payout method.
   */
  async connectStripe(userPublicId: string, code: string): Promise<void> {
    await this._post(
      `payouts/v1/users/${userPublicId}/payout-method/connect/stripe`,
      { code },
    )
  }
}

/**
 * API for working with pro payout analytics.
 */
export class ProAnalyticsApi extends ApiClient {
  /**
   * Gets the current user's payout summary.
   */
  async getProPayoutSummary(
    signal?: AbortSignal,
  ): Promise<ProPayoutSummaryResource> {
    return this._get(`payouts/v1/analytics/summary`, undefined, { signal })
  }
}

/**
 * API for working with transactions (Pro perspective).
 */
export class ProTransactionApi extends ApiClient {
  listHistoricalTransactions(
    request: ListProHistoricalTransactionsRequest,
    signal?: AbortSignal,
  ): Promise<AipCursorPaged<ProHistoricalTransactionResource>> {
    const params = toURLSearchParams({
      page_size: request.page_size,
      page_token: request.page_token,
      order_by: request.order_by,
      search_text: request.search_text,
      transaction_source: request.transaction_source,
      date_completed_from: request.date_completed_from,
      date_completed_to: request.date_completed_to,
      workspace_ids: request.workspace_ids,
    })
    return this._get('payouts/v1/transactions', params, {
      signal,
    })
  }

  async downloadHistoricalTransactionsAsCsv(
    request: DownloadProHistoricalTransactionsAsCsvRequest,
  ): Promise<ReadableStream<Uint8Array>> {
    // Need to use `fetch` to get a stream.
    const params = new URLSearchParams({
      ...(request.search_text && { search_text: request.search_text }),
      ...(request.workspace_ids && { workspace_ids: request.workspace_ids }),
      ...(request.transaction_source && {
        transaction_source: request.transaction_source,
      }),
      ...(request.date_completed_from && {
        date_completed_from: request.date_completed_from.toISOString(),
      }),
      ...(request.date_completed_to && {
        date_completed_to: request.date_completed_to.toISOString(),
      }),
    })
    const httpRequest = new Request(
      this._createURL('payouts/v1/transactions.csv', params),
      { method: 'GET' },
    )
    const response = await this._send(httpRequest).then(
      ApiClient.throwIfNotSuccessful,
    )

    return response.body!
  }
}
